import { Injectable, Signal, signal } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'

import { Observable, fromEvent, merge } from 'rxjs'
import { filter, share, skip, tap } from 'rxjs/operators'

import { cancelBrowserShortcut, isMacOS } from '@flowx/shared/utils'

@Injectable({
  providedIn: 'root',
})
export class KeyboardService {
  #isMetaPressed = signal(false)
  // skip(1) drops toObservable's replay of the current value, leaving only real transitions
  #metaKeyState: Observable<boolean> = toObservable(this.#isMetaPressed).pipe(skip(1), share())

  isMetaPressed: Signal<boolean> = this.#isMetaPressed.asReadonly()

  keyup: Observable<KeyboardEvent> = fromEvent(document, 'keyup').pipe(
    share()
  ) as Observable<KeyboardEvent>

  keydown: Observable<KeyboardEvent> = fromEvent(document, 'keydown').pipe(
    share()
  ) as Observable<KeyboardEvent>

  copy: Observable<KeyboardEvent> = fromEvent(document, 'copy').pipe(
    share()
  ) as Observable<KeyboardEvent>

  paste: Observable<KeyboardEvent> = fromEvent(document, 'paste').pipe(
    share()
  ) as Observable<KeyboardEvent>

  arrowUp: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(({ key }) => key === 'ArrowUp'),
    share()
  ) as Observable<KeyboardEvent>

  arrowDown: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(({ key }) => key === 'ArrowDown'),
    share()
  ) as Observable<KeyboardEvent>

  escape: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(({ key }) => key === 'Escape'),
    share()
  ) as Observable<KeyboardEvent>

  enter: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(({ key }) => key === 'Enter'),
    share()
  ) as Observable<KeyboardEvent>

  metaK: Observable<KeyboardEvent> = this.keydown.pipe(
    filter((event) => event.key === 'k' && (isMacOS ? event.metaKey : event.ctrlKey)),
    tap(cancelBrowserShortcut),
    share()
  ) as Observable<KeyboardEvent>

  metaG: Observable<KeyboardEvent> = this.keydown.pipe(
    filter((event) => event.key === 'g' && (isMacOS ? event.metaKey : event.ctrlKey)),
    tap(cancelBrowserShortcut),
    share()
  ) as Observable<KeyboardEvent>

  mouseup: Observable<MouseEvent> = fromEvent(document, 'mouseup').pipe(
    share()
  ) as Observable<MouseEvent>

  mousedown: Observable<MouseEvent> = fromEvent(document, 'mousedown').pipe(
    share()
  ) as Observable<MouseEvent>

  metaKeyDown: Observable<boolean> = this.#metaKeyState.pipe(filter(Boolean), share())

  metaKeyUp: Observable<boolean> = this.#metaKeyState.pipe(
    filter((isMetaPressed) => !isMetaPressed),
    share()
  )

  // TODO create a generic method to handle all the keyboard events and pass the key and the event type as parameters
  undo: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(
      ({ key, shiftKey, metaKey, ctrlKey }) =>
        (key === 'z' || key === 'Z') && !shiftKey && (isMacOS ? metaKey : ctrlKey)
    )
  )
  redo: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(
      ({ key, shiftKey, metaKey, ctrlKey }) =>
        (key === 'z' || key === 'Z') && shiftKey && (isMacOS ? metaKey : ctrlKey)
    )
  )

  delete: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(({ key }) => key === 'Delete'),
    share()
  ) as Observable<KeyboardEvent>

  backspace: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(({ key }) => key === 'Backspace'),
    share()
  ) as Observable<KeyboardEvent>

  createComponent: Observable<KeyboardEvent> = this.keydown.pipe(
    filter(
      ({ code, altKey, metaKey, ctrlKey }) =>
        code === 'KeyT' && altKey && (isMacOS ? metaKey : ctrlKey)
    ),
    share()
  )

  constructor() {
    this.#trackMetaKeyState()
  }

  isMultiSelectModifierPressed({
    metaKey,
    ctrlKey,
    shiftKey,
  }: KeyboardEvent | MouseEvent): boolean {
    return !shiftKey && (isMacOS ? metaKey : ctrlKey)
  }

  #trackMetaKeyState(): void {
    merge(this.keydown, this.keyup, this.mousedown)
      .pipe(takeUntilDestroyed())
      .subscribe((event) => this.#isMetaPressed.set(this.isMultiSelectModifierPressed(event)))

    // The keyup is delivered to whatever gained focus, not to us, so a chord that switches tab or
    // app (⌘+Tab, ⌘+`, ⌘+Shift+]) would otherwise leave the modifier latched on when we come back.
    merge(fromEvent(window, 'blur'), fromEvent(document, 'visibilitychange'))
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.#isMetaPressed.set(false))
  }
}
